/**
 * Shared helper for computing the client-visible price of a snapshot unit.
 *
 * `OfferSnapshotUnitPrice.clientPriceNet` is the raw (pre-discount) net price;
 * `discountPercent` is applied on top of it via the shared
 * `computeDiscountedPrice` helper (@ooh/contracts), which mirrors the
 * `lineTotal` formula used in the merged offer table's price columns (see
 * apps/web/src/features/offers/units-tab/lib/offer-unit-price-columns.tsx —
 * `lineTotal = clientPriceNet * (1 - discountPercent / 100)`), so the
 * discount a salesperson applies there is reflected consistently on both
 * the merged table and any client-facing preview/offer view.
 */
import { computeDiscountedPrice, type OfferSnapshotUnit } from '@ooh/contracts';

interface UnitWithPrices {
  prices: OfferSnapshotUnit['prices'];
}

/** Returns the discounted net price for the unit's first price row, or
 * `undefined` when there is no price row or `clientPriceNet` is null. */
export function getSnapshotClientPrice(unit: UnitWithPrices): number | undefined {
  const price = unit.prices[0];
  if (price?.clientPriceNet == null) return undefined;
  return computeDiscountedPrice(price.clientPriceNet, price.discountPercent);
}
